import { Component, OnInit, Inject, Input } from '@angular/core';
import { EmergencySchedule } from "../../shared/models/emergency/schedule.model";
import { EmergencyActivity } from "../../shared/models/emergency/activity.model";
import { EmergencyTier } from "../../shared/models/emergency/tier.model";
import { EmergencyGroup } from "../../shared/models/emergency/group.model";
import { EmergencyService } from "../../services/emergency.service";
import { ModalService } from "../../services/modal.service";
import { AdHocActivitiesModalComponent } from "./ad-hoc/adhoc-activities-modal.component";

@Component({
    selector: 'app-emergency-activities',
    templateUrl: './activities.component.html',
    styleUrls: ['./activities.component.scss']
})
export class EmergencyActivitiesComponent implements OnInit {

    tiers: Array<any> = [];
    tiersList: Array<any> = [];
    schedules: EmergencySchedule[] = new Array<EmergencySchedule>();
    originalSchedules: EmergencySchedule[] = new Array<EmergencySchedule>();
    groups: EmergencyGroup[] = new Array<EmergencyGroup>();
    fte: number;

    constructor(private emergencyService: EmergencyService, private modalService: ModalService) { }

    ngOnInit() {
        this.schedules = this.emergencyService.emergencySchedules;
        this.originalSchedules = this.schedules.map(activity => Object.assign(new EmergencySchedule(), activity));
        this.tiers = this.emergencyService.emergencyTiers;
        this.tiersList = this.prepareTiersList(this.tiers);
        this.fte = this.emergencyService.fte;
        this.groupActivities();
        this.emergencyService.cancelChanges = this.cancelChanges;
        this.emergencyService.updateViewAfterSave = this.updateViewAfterSave;
        this.emergencyService.getCheckedActivities = this.getCheckedActivities;
        this.emergencyService.clearAll = this.clearAll;
        this.emergencyService.getAdHocActivities = this.getAdHocActivities;
    }

    public cancelChanges = () => {
        this.schedules = this.originalSchedules.map(activity => Object.assign(new EmergencySchedule(), activity));
        this.groupActivities();
        this.emergencyService.activitiesChanged = false;
    }

    public updateViewAfterSave = () => {
        this.originalSchedules = this.schedules.map(schedule => Object.assign(new EmergencySchedule(), schedule));
        this.emergencyService.activitiesChanged = false;
    }

    private prepareTiersList(tiers: Array<any>) {
        let list = [];
        tiers.forEach((item, index) => {
            list.push({
                tierName: tiers.slice(0, index + 1).map(tier => tier.tierName).join(', ') + ' only',
                tierSequence: item.tierSequence
            });
        });
        return list;
    }

    private getCheckedActivities = (): Array<number> => {
        let schedules = new Array<EmergencySchedule>();
        this.groups.forEach(group => {
            group.tiers.forEach(tier => {
                tier.activities.forEach(activity => {
                    schedules = schedules.concat(activity.schedules.filter(schedule => schedule.checked));
                });
            });
        });
        return schedules.map(schedule => schedule.scheduleId);
    }

    private getAdHocActivities = (): Array<any> => {
        let adHocSchedules = new Array<any>();
        this.groups.forEach(group => {
            group.adHocTier.activities.forEach(activity => {
                adHocSchedules.push({
                    activityId: activity.activityId,
                    activityName: activity.activityName,
                    duration: activity.staffNeeded * this.fte * 60 / group.groupLocationCount,
                    groupName: group.groupName
                });
            });
        });
        return adHocSchedules;
    }

    public expandAll() {
        this.groups.forEach(group => {
            group.expanded = true;
            group.tiers.forEach(tier => {
                tier.expanded = true;
            });
            group.adHocTier.expanded = true;
        });
    }

    public collapseAll() {
        this.groups.forEach(group => {
            group.expanded = false;
            group.tiers.forEach(tier => {
                tier.expanded = false;
            });
            group.adHocTier.expanded = false;
        });
    }

    public checkGroup(group: EmergencyGroup) {
        if (group.tiers.find(tier => !!tier.activities.find(activity => !activity.checked))) {
            group.tiers.forEach(tier => {
                tier.activities.forEach(activity => {
                    this.checkActivity(activity, true);
                });
                this.tierChanged(tier);
            });
        } else {
            group.tiers.forEach(tier => {
                tier.activities.forEach(activity => {
                    this.checkActivity(activity, false)
                });
                this.tierChanged(tier);
            });
        }
        this.groupChanged(group);
    }

    public checkTier(tier: EmergencyTier, group: EmergencyGroup) {
        if (tier.activities.filter(activity => activity.checked).length !== tier.activities.length) {
            tier.activities.forEach(activity => this.checkActivity(activity, true));
        } else {
            tier.activities.forEach(activity => this.checkActivity(activity, false));
        }
        this.groupChanged(group);
    }

    public groupChanged(group: EmergencyGroup) {
        group.tiers.forEach(tier => this.tierChanged(tier));
        let checkedCount = group.tiers.filter(tier => tier.checked).length;
        let indeterminateCount = group.tiers.filter(tier => tier.indeterminate).length;
        group.checked = checkedCount === group.tiers.length;
        group.indeterminate = indeterminateCount > 0 || (checkedCount > 0 && checkedCount < group.tiers.length);
    }

    public tierChanged(tier: EmergencyTier) {
        let count = tier.activities.filter(activity => activity.checked).length;
        tier.checked = count === tier.activities.length;
        tier.indeterminate = count > 0 && count < tier.activities.length;
    }

    private groupActivities() {
        this.groups = [];
        
        let groupNames = Array.from(new Set(this.schedules.map(schedule => schedule.groupName)));
        groupNames.forEach(groupName => {
            let group: EmergencyGroup = {
                groupName: groupName,
                groupLocationCount: this.schedules.find(schedule => schedule.groupName === groupName).groupLocationCount,
                tiers: Array.from(new Set(this.schedules.filter(schedule => !schedule.isAdHocActivity).filter(schedule => schedule.groupName === groupName).map(item => item.tierName))).sort(this.sortTiers)
                    .map(tierName => {
                        return {
                            tierName: tierName,
                            activities: Array.from(new Set(this.schedules.filter(schedule => !schedule.isAdHocActivity).filter(schedule => schedule.groupName === groupName && schedule.tierName === tierName).sort(this.sortActivities).map(item => item.activityId)))
                            .map((activityId) => {
                                return {
                                    activityId: activityId,
                                    activityName: this.schedules.find(schedule => schedule.groupName === groupName && schedule.tierName === tierName && schedule.activityId === activityId).activityName,
                                    staffNeeded: this.getDurationSum(this.schedules.filter(schedule => schedule.groupName === groupName && schedule.tierName === tierName && schedule.activityId === activityId)),
                                    schedules: this.schedules.filter(schedule => schedule.groupName === groupName && schedule.tierName === tierName && schedule.activityId === activityId),
                                    checked: this.schedules.find(schedule => schedule.groupName === groupName && schedule.tierName === tierName && schedule.activityId === activityId).checked
                                }
                            })
                        }
                    }),
                adHocTier: {
                    tierName: 'Ad hoc activities',
                    activities: Array.from(new Set(this.schedules.filter(schedule => schedule.groupName === groupName && schedule.isAdHocActivity).sort(this.sortActivities).map(item => item.activityId)))
                        .map((activityId) => {
                            return {
                                activityId: activityId,
                                activityName: this.schedules.find(schedule => schedule.groupName === groupName && schedule.isAdHocActivity && schedule.activityId === activityId).activityName,
                                staffNeeded: this.getDurationSum(this.schedules.filter(schedule => schedule.groupName === groupName && schedule.isAdHocActivity && schedule.activityId === activityId)),
                                schedules: this.schedules.filter(schedule => schedule.groupName === groupName && schedule.isAdHocActivity && schedule.activityId === activityId),
                                checked: true
                            }
                        })
                }
            };
            this.groupChanged(group);
            this.groups.push(group);
        });
    }

    private sortTiers = (tierName1, tierName2) => {
        let tier1 = this.tiers.find(item => item.tierName === tierName1);
        let tier2 = this.tiers.find(item => item.tierName === tierName2);

        if (!tier1) {
            return 1;
        } 
        if (!tier2) {
            return -1;
        }
        if (tier1.tierSequence < tier2.tierSequence) {
            return -1;
        }
        if (tier1.tierSequence > tier2.tierSequence) {
            return 1;
        }
        return 0;
    }

    private sortActivities = (activity1, activity2) => {
        return activity1.activityName.toLowerCase().localeCompare(activity2.activityName.toLowerCase());
    }

    private getDurationSum(schedules: EmergencySchedule[]): number {
        let sum = 0;
        schedules.forEach(schedule => {
            sum = sum + schedule.staffNeeded;
        });
        return sum;
    }

    public selectAll() {
        this.groups.forEach(group => {
            group.tiers.forEach(tier => {
                tier.activities.forEach(activity => {
                    this.checkActivity(activity, true);
                });
            });
            this.groupChanged(group);
        });
    }

    public selectTier(tierSequence) {
        let tierNames = this.tiers.filter(tier => (tier.tierSequence <= tierSequence)).map(tier => tier.tierName);
        this.groups.forEach(group => {
            group.tiers.forEach(tier => {
                if (tierNames.find(item => item === tier.tierName)) {
                    tier.activities.forEach(activity => this.checkActivity(activity, true));
                }
                else {
                    tier.activities.forEach(activity => this.checkActivity(activity, false));
                }
            });
            this.groupChanged(group);
        });
    }

    public clearAll = () => {
        this.groups.forEach(group => {
            group.tiers.forEach(tier => {
                tier.activities.forEach(activity => {
                    this.checkActivity(activity, false);
                });
            });
            this.groupChanged(group);
        });
    }

    public openAdHocModal(group: EmergencyGroup) {

        var modal = this.modalService.OpenModal(AdHocActivitiesModalComponent)
        modal.componentInstance.title = 'Add ad-hoc activity to ' + group.groupName;

        modal.result.then(result => {
            group.adHocTier.activities.push({
                activityId: null,
                activityName: result.activity.activityName,
                staffNeeded: result.activity.duration / this.fte / 60 * group.groupLocationCount,
                checked: true,
                schedules: []
            });
            this.activitiesChanged();
        });
    }

    public openEditAdHocModal(activity: EmergencyActivity, group: EmergencyGroup) {

        var modal = this.modalService.OpenModal(AdHocActivitiesModalComponent)
        modal.componentInstance.title = 'Editing ad-hoc activity for ' + group.groupName;
        modal.componentInstance.name = activity.activityName;
        modal.componentInstance.duration = activity.staffNeeded * this.fte * 60 / group.groupLocationCount;
        modal.componentInstance.originalName = modal.componentInstance.name;
        modal.componentInstance.originalDuration = modal.componentInstance.duration;

        modal.result.then(result => {
            activity.activityName = result.activity.activityName;
            activity.staffNeeded = result.activity.duration / this.fte / 60 * group.groupLocationCount;
            this.activitiesChanged();
        });

    }

    public deleteAdHoc(activity: EmergencyActivity, tier: EmergencyTier) {

        tier.activities = tier.activities.filter(item => item !== activity);
        this.activitiesChanged();
    }

    public groupSum(group: EmergencyGroup) {
        let sum = 0;
        group.tiers.forEach(tier => {
            sum += this.tierSum(tier);
        });
        sum += this.tierSum(group.adHocTier);
        return sum;
    }

    public tierSum(tier: EmergencyTier) {
        let sum = 0;
        tier.activities.forEach(activity => {
            if (activity.checked) {
                sum += activity.staffNeeded;
            }
        });
        return sum;
    }

    public getSumStaffNeeded() {
        let sum = 0;
        this.groups.forEach(group => {
            sum += this.groupSum(group);
        });
        return sum;
    }

    public activitiesChanged() {
        this.emergencyService.activitiesChanged = true;
    }

    public checkActivity(activity: EmergencyActivity, value: boolean) {
        activity.checked = value;
        activity.schedules.forEach(schedule => schedule.checked = value);
    }
}

